import { useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";
import {
  EMPTY_CONTEXT,
  useBaseBooleanFeatureFlag,
  useBaseVariantFeatureFlag,
} from "@bto/shared";
import { BatchedSetAnalyticsUser } from "../../services/Analytics";
import { patientState } from "../../recoil/atoms";
import { getSanitizedUserData } from "../contextUtils";

/**
 * React hook to check if a boolean feature flag is enabled for the current patient session.
 *
 * Resolves patient identity from Recoil state and delegates LaunchDarkly evaluation
 * to the shared {@link useBaseBooleanFeatureFlag} hook.
 *
 * @param flagKey The LaunchDarkly flag key to evaluate
 * @param additionalContext Extra context merged in for this specific evaluation (triggers identify)
 * @param entityIdInput Optional entity ID override; defaults to the patient's auth0 user ID
 */
export const useBooleanFeatureFlag = (
  flagKey: string,
  additionalContext: Record<string, unknown> = EMPTY_CONTEXT,
  entityIdInput?: string,
): { enabled: boolean; loading: boolean } => {
  const patientInfo = useRecoilValue(patientState);

  const entityId = entityIdInput ?? patientInfo?.auth0.user_id;

  const userContext = useMemo(
    () => (patientInfo ? getSanitizedUserData(patientInfo) : EMPTY_CONTEXT),
    [patientInfo],
  );

  // Flush any pending batched analytics on unmount so in-flight flag
  // evaluations are not silently dropped during SPA navigation or page unload.
  useEffect(() => () => BatchedSetAnalyticsUser.flush(), []);

  return useBaseBooleanFeatureFlag(
    flagKey,
    entityId,
    userContext,
    additionalContext,
    (enabled) => {
      if (patientInfo && entityId) {
        BatchedSetAnalyticsUser(patientInfo, entityId, {
          [flagKey]: enabled.toString(),
        });
      }
    },
  );
};

/**
 * React hook to check a variant (string-valued) feature flag for the current patient session.
 *
 * Resolves patient identity from Recoil state and delegates LaunchDarkly evaluation
 * to the shared {@link useBaseVariantFeatureFlag} hook.
 *
 * @param flagKey The LaunchDarkly flag key to evaluate
 * @param additionalContext Extra context merged in for this specific evaluation (triggers identify)
 * @param entityIdInput Optional entity ID override; defaults to the patient's auth0 user ID
 */
export const useVariantFeatureFlag = (
  flagKey: string,
  additionalContext: Record<string, unknown> = EMPTY_CONTEXT,
  entityIdInput?: string,
): { variant: string; loading: boolean } => {
  const patientInfo = useRecoilValue(patientState);

  const entityId = entityIdInput ?? patientInfo?.auth0.user_id;

  const userContext = useMemo(
    () => (patientInfo ? getSanitizedUserData(patientInfo) : EMPTY_CONTEXT),
    [patientInfo],
  );

  useEffect(() => () => BatchedSetAnalyticsUser.flush(), []);

  return useBaseVariantFeatureFlag(
    flagKey,
    entityId,
    userContext,
    additionalContext,
    (variant) => {
      if (patientInfo && entityId) {
        BatchedSetAnalyticsUser(patientInfo, entityId, { [flagKey]: variant });
      }
    },
  );
};
